import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import Navbar from "@/components/layout/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  CreditCard,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  Wallet,
  Receipt,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

const ITEMS_PER_PAGE = 30;

interface CreditOrder {
  id: string;
  credits_amount: number;
  price_thb: number;
  bonus_credits: number;
  payment_method: string;
  status: string;
  package_name: string | null;
  created_at: string;
  completed_at: string | null;
}

const CreditHistory = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState<CreditOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Total stats for completed orders
  const [totalCreditsReceived, setTotalCreditsReceived] = useState(0);
  const [totalAmountSpent, setTotalAmountSpent] = useState(0);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login");
    }
  }, [user, authLoading, navigate]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [showAll]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!user) return;

      setIsLoading(true);

      // First, get total count
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const supabaseAny = supabase as any;

      let countQuery = supabaseAny
        .from("credit_orders")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);

      if (!showAll) {
        countQuery = countQuery.eq('status', 'completed');
      }

      const { count } = await countQuery;
      setTotalCount(count || 0);

      // Then fetch paginated data
      const from = (currentPage - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      let query = supabaseAny
        .from("credit_orders")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .range(from, to);

      if (!showAll) {
        query = query.eq('status', 'completed');
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching orders:", error);
      } else {
        setOrders((data as CreditOrder[]) || []);
      }
      setIsLoading(false);
    };

    if (user) {
      fetchOrders();
    }
  }, [user, showAll, currentPage]);

  // Fetch total stats for all completed orders (separate from pagination)
  useEffect(() => {
    const fetchTotalStats = async () => {
      if (!user) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const supabaseAny = supabase as any;

      const { data, error } = await supabaseAny
        .from("credit_orders")
        .select("credits_amount, bonus_credits, price_thb")
        .eq("user_id", user.id)
        .eq("status", "completed");

      if (error) {
        console.error("Error fetching total stats:", error);
        return;
      }

      if (data) {
        const totalCredits = data.reduce((sum: number, o: any) => sum + (o.credits_amount || 0) + (o.bonus_credits || 0), 0);
        const totalSpent = data.reduce((sum: number, o: any) => sum + (o.price_thb || 0), 0);
        setTotalCreditsReceived(totalCredits);
        setTotalAmountSpent(totalSpent);
      }
    };

    if (user) {
      fetchTotalStats();
    }
  }, [user]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case "pending":
      case "processing":
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case "failed":
      case "cancelled":
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "completed":
        return t("สำเร็จ", "Completed");
      case "pending":
        return t("รอชำระ", "Pending");
      case "processing":
        return t("กำลังตรวจสอบ", "Processing");
      case "failed":
        return t("ล้มเหลว", "Failed");
      case "cancelled":
        return t("ยกเลิก", "Cancelled");
      default:
        return status;
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case "stripe_promptpay":
        return "PromptPay (Stripe)";
      case "manual":
        return t("โอนเงิน", "Bank Transfer");
      case "promo":
        return t("โปรโมชั่น", "Promo");
      case "coupon":
        return t("คูปอง", "Coupon");
      default:
        return method;
    }
  };

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-20 pb-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
                <Wallet className="h-8 w-8 text-primary" />
                {t("ประวัติการเงิน", "Transaction History")}
              </h1>
              <p className="text-muted-foreground">{t("ดูประวัติการซื้อเครดิตทั้งหมด", "View all credit purchase history")}</p>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Card variant="glass">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t("เครดิตที่เติมทั้งหมด", "Total Credits Received")}</p>
                    <p className="text-xl font-bold text-green-500">{totalCreditsReceived.toLocaleString()} {t("เครดิต", "Credits")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card variant="glass">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Wallet className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t("ยอดเติมเงินทั้งหมด", "Total Amount Spent")}</p>
                    <p className="text-xl font-bold text-primary">฿{totalAmountSpent.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Orders List */}
          <Card variant="glass">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                {t("รายการซื้อเครดิต", "Credit Purchases")}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAll(!showAll)}
                className="gap-2"
              >
                {showAll ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    {t("แสดงเฉพาะรายการสำเร็จ", "Show Completed Only")}
                  </>
                ) : (
                  <>
                    <Receipt className="h-4 w-4" />
                    {t("แสดงทั้งหมด", "Show All")}
                  </>
                )}
              </Button>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">{t("ยังไม่มีประวัติการซื้อ", "No purchase history yet")}</p>
                  <Link to="/pricing">
                    <Button variant="glow">
                      {t("ซื้อเครดิต", "Buy Credits")}
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order) => (
                    <div
                      key={order.id}
                      className="flex items-center justify-between p-4 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors border border-border/50"
                    >
                      <div className="flex items-center gap-4">
                        {getStatusIcon(order.status)}
                        <div>
                          <p className="font-semibold">
                            {order.package_name || `${order.credits_amount.toLocaleString()} ${t("เครดิต", "Credits")}`}
                          </p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span>{getPaymentMethodLabel(order.payment_method)}</span>
                            <span>•</span>
                            <span>{formatDate(order.created_at)}</span>
                          </div>
                          {order.bonus_credits > 0 && (
                            <p className="text-xs text-green-500 mt-1">
                              +{order.bonus_credits.toLocaleString()} {t("โบนัส", "bonus")}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold">฿{order.price_thb.toLocaleString()}</p>
                        <p className={`text-sm ${order.status === "completed" ? "text-green-500" :
                          order.status === "pending" || order.status === "processing" ? "text-yellow-500" :
                            "text-red-500"
                          }`}>
                          {getStatusLabel(order.status)}
                        </p>
                      </div>
                    </div>
                  ))}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4 border-t border-border/50">
                      <p className="text-sm text-muted-foreground">
                        {t("หน้า", "Page")} {currentPage} {t("จาก", "of")} {totalPages} ({totalCount} {t("รายการ", "items")})
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handlePrevPage}
                          disabled={currentPage <= 1}
                          className="gap-1"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          {t("ก่อนหน้า", "Prev")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleNextPage}
                          disabled={currentPage >= totalPages}
                          className="gap-1"
                        >
                          {t("ถัดไป", "Next")}
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default CreditHistory;
